import React from "react";
import PropTypes from "prop-types";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useRouter } from "next/router";

import { QUERY_KEYS, USER_TYPES, ROUTE_PREFIX } from "../../constants";
import api from "../../api";
import { useAppConfig, appConfigActionsTypes } from "../appConfig";
import BugsnagClient from "../../utils/bugsnag";

const missingUserProvider = "You forgot to wrap your app in <UserProvider>";

export const UserContext = React.createContext({
  get user() {
    throw new Error(missingUserProvider);
  },
  get error() {
    throw new Error(missingUserProvider);
  },
  get isLoading() {
    throw new Error(missingUserProvider);
  },
});
UserContext.displayName = "UserContext";

export const useUser = () => React.useContext(UserContext);

const UserProvider = ({ children, initialUser }) => {
  const client = api();
  const queryClient = useQueryClient();
  const router = useRouter();
  const [, appConfigDispatch] = useAppConfig();

  const { data: user, isLoading: isGettingUser } = useQuery(
    QUERY_KEYS.USER,
    client.getUser,
    {
      initialData: initialUser,
      refetchOnWindowFocus: true,
      select: (undecoratedUser) => ({
        ...undecoratedUser,
        isAnonymous:
          undecoratedUser.userTypeId === USER_TYPES.ANONYMOUS.id ||
          undecoratedUser.userTypeId === USER_TYPES.ANONYMOUS_UNIVERSE.id,
        isFamily: undecoratedUser.userTypeId === USER_TYPES.FAMILY.id,
        isUnregisteredFamily:
          undecoratedUser.userTypeId === USER_TYPES.UNREGISTERED_FAMILY.id,
        isUnregisteredTeacher:
          undecoratedUser.userTypeId === USER_TYPES.UNREGISTERED_TEACHER.id,
        isClassroomTeacher:
          undecoratedUser.userTypeId === USER_TYPES.CLASSROOM_TEACHER.id,
        isUserRegistrationEnabled: !!undecoratedUser.features?.registration,
        isLoggedIn: [
          USER_TYPES.FAMILY.id,
          USER_TYPES.CLASSROOM_TEACHER.id,
        ].includes(undecoratedUser.userTypeId),
      }),
      onSuccess: (undecoratedUser) => {
        BugsnagClient.setUser(undecoratedUser.id, undecoratedUser.email);
      },
    },
  );

  const {
    mutate: updateUser,
    isLoading: isUpdatingUser,
    error: updateError,
  } = useMutation((newUserProperties) => client.updateUser(newUserProperties), {
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEYS.USER);
    },
  });

  const { mutate: updateUserFlags } = useMutation(
    (newUserFlags) => client.updateUserFlags(newUserFlags),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEYS.USER);
      },
    },
  );

  const { mutate: logout, isLoading: isLoggingOut } = useMutation(
    () => client.logout(),
    {
      onSuccess: () => {
        router
          .replace(ROUTE_PREFIX.HOME)
          .then(() => {
            queryClient.invalidateQueries(QUERY_KEYS.USER);
          })
          .then(() => {
            appConfigDispatch({
              type: appConfigActionsTypes.SET_LOGOUT_TOAST,
              payload: true,
            });
          });
      },
    },
  );

  const isLoading = [isGettingUser, isUpdatingUser, isLoggingOut].includes(
    true,
  );

  return (
    <UserContext.Provider
      value={{
        user,
        isLoading,
        errors: { updateError },
        updateUser,
        updateUserFlags,
        logout,
      }}
    >
      {user ? children : null}
    </UserContext.Provider>
  );
};

UserProvider.propTypes = {
  initialUser: PropTypes.shape({}),
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

UserProvider.defaultProps = {
  initialUser: undefined,
  children: undefined,
};

export default UserProvider;
